import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { TextInput } from '@/modules/base/components/inputs'
import BaseDrawer from '@/components/drawers/base-drawer'
import CompanyFormInput from '@/modules/settings/components/tax-settings/components/companyFormSelectInput'
import { UserSelect } from '@/modules/user/components/user-select/user-select'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import IconCheck from 'assets/images/icons/check.svg'
import lang from '@/lang'
import { CLIENT_INVITATION_STATUS } from '../../_helpers/getStatusLabel'
import { toast } from '@/modules/base/components/toast/toastConfig'

const useStyles = makeStyles((theme) => ({
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '10px 0',
  },
  input: {
    marginBottom: 20,
  },
  contactPersonHeading: {
    fontSize: theme.fontSizes.medium,
    fontWeight: theme.fontWeights.medium,
  },
  companyFormInput: {
    width: '100% !important',
    paddingRight: 0,
  },
  row: {
    display: 'flex',
    gap: 20,
  },
}))

const ClientOverViewEditDrawer = ({ client, open = false, onSubmit, onClose }) => {
  const classes = useStyles()
  const { inviteeStatus } = client
  const isOfflineClient = inviteeStatus === CLIENT_INVITATION_STATUS.OFFLINE

  const onCloseDrawer = (setFieldValue) => {
    if (client?.clerks) {
      setFieldValue('clerks', client?.clerks)
    }
    onClose && onClose()
  }
  const handleOnSubmit = async(values, setSubmitting) => {
    const { companyName, companyForm, clientNumber, clerks, responsibleUser } = values
    const data = {
      companyName,
      companyForm,
      clientNumber: clientNumber || '',
      clerks: (clerks.length && clerks.map(c => c.value)) ?? undefined,
    }
    if (!isOfflineClient) data.responsibleUser = responsibleUser?.value
    await onSubmit(data)
    setSubmitting(false)
    toast.showToast({
      type: 'success',
      message: lang.clientDetail.widgets.overviewWidget.drawer.success,
    })
    onClose()
  }

  const initialValues = {
    companyName: client?.companyName,
    companyForm: client?.companyForm,
    clientNumber: client?.clientNumber,
    clerks: client?.clerks || [],
    responsibleUser: client?.responsibleUser && { label: `${client?.responsibleUser?.firstName} ${client?.responsibleUser?.lastName} (${client?.responsibleUser?.email})`, value: client?.responsibleUser?.id },
  }

  const validationSchema = Yup.object({
    companyName: Yup.string().required(lang.clientDetail.widgets.overviewWidget.drawer.errors.companyNameRequired),
    clerks: Yup.array().required(lang.clientDetail.widgets.overviewWidget.drawer.errors.clerkRequired),
    companyForm: Yup.string().required(lang.clientDetail.widgets.overviewWidget.drawer.errors.companyFormRequired),
  })

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={(values, { setSubmitting }) => handleOnSubmit(values, setSubmitting)}
    >
      {({ handleSubmit, isSubmitting, setFieldValue, values, touched, errors, isValid }) => (
        <BaseDrawer open={open} onClose={() => onCloseDrawer(setFieldValue)} headerChildren={lang.clientDetail.widgets.overviewWidget.drawer.headline} footerChildren={
          <div className={classes.buttonWrapper}>
            <ButtonIconSquare
              size='large'
              icon={IconCheck}
              label={lang.generics.save}
              onClick={handleSubmit}
              disabled={isSubmitting || !isValid}
              type="submit"
            />
          </div>
        }>
          <TextInput name={'companyName'} label={lang.clientDetail.widgets.overviewWidget.drawer.companyName} className={classes.input} useFormik />
          <CompanyFormInput name='companyForm' wrapperClassName={classes.companyFormInput} useFormik />
          <div className={classes.row}>
            <TextInput name={'clientNumber'} label={lang.clientDetail.widgets.overviewWidget.drawer.clientNumber} className={classes.input} useFormik />
            <UserSelect
              name="clerks"
              className={classes.userSelect}
              label={lang.clientDetail.widgets.overviewWidget.drawer.clerk}
              customSelectOptions={{ isMulti: true }}
              onChange={value => setFieldValue('clerks', value)}
              value={values?.clerks}
              errorMessage={touched?.clerks ? errors?.clerks : undefined}
            />
          </div>
          <hr />
          {!isOfflineClient && (
            <>
              <p className={classes.contactPersonHeading}>{lang.clientDetail.widgets.overviewWidget.drawer.contantPerson}</p>
              <UserSelect
                hideTaxConsultantUsers
                name="responsibleUser"
                hasClientModuleAccess={true}
                tenantCollaborationId={client?.id}
                label={lang.generics.name}
                value={values.responsibleUser}
                onChange={value => setFieldValue('responsibleUser', value)}
                errorMessage={touched?.responsibleUser ? errors?.responsibleUser?.value : undefined}
              />
            </>
          )}
        </BaseDrawer>
      )
      }
    </Formik>
  )
}

ClientOverViewEditDrawer.propTypes = {
  client: PropTypes.object,
  open: PropTypes.bool,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
}

ClientOverViewEditDrawer.defaultProps = {
  client: {},
  open: false,
  onSubmit: () => {},
  onClose: () => {},
}

export default ClientOverViewEditDrawer
